import { z } from "zod"
import { isEmailBlacklisted } from "@/shared/lib/email-blacklist"
import { PASSWORD_MIN, PASSWORD_MAX } from "@/shared/lib/password"

export const loginSchema = z.object({
  email: z.string().email("Please enter a valid email"),
  password: z.string().min(8, "Password must be at least 8 characters"),
})

export const passwordRequirements = [
  { key: "length", label: "8–250 characters", regex: new RegExp(`^.{${PASSWORD_MIN},${PASSWORD_MAX}}$`) },
  { key: "letter", label: "At least one letter", regex: /[a-zA-Z]/ },
  { key: "number", label: "At least one number", regex: /\d/ },
] as const

export function getPasswordStrength(password: string): {
  score: number
  label: string
  color: string
} {
  const met = passwordRequirements.filter((r) => r.regex.test(password)).length
  if (password.length === 0) return { score: 0, label: "", color: "" }
  if (met <= 1) return { score: 1, label: "Weak", color: "bg-red-500" }
  if (met === 2) return { score: 2, label: "Fair", color: "bg-orange-500" }
  return { score: 3, label: "Strong", color: "bg-green-500" }
}

export const registerSchema = z
  .object({
    email: z.string().email("Please enter a valid email"),
    password: z
      .string()
      .min(PASSWORD_MIN, "Password must be at least 8 characters")
      .max(PASSWORD_MAX, "Password must be at most 250 characters")
      .regex(/[a-zA-Z]/, "Password must contain at least one letter")
      .regex(/\d/, "Password must contain at least one number"),
  })
  .refine((data) => !isEmailBlacklisted(data.email), {
    message: "This email domain is not allowed.",
    path: ["email"],
  })

export const forgotPasswordSchema = z
  .object({
    email: z.string().email("Please enter a valid email"),
  })
  .refine((data) => !isEmailBlacklisted(data.email), {
    message: "This email domain is not allowed.",
    path: ["email"],
  })

export const resetPasswordSchema = z
  .object({
    password: z
      .string()
      .min(PASSWORD_MIN, "Password must be at least 8 characters")
      .max(PASSWORD_MAX, "Password must be at most 250 characters")
      .regex(/[a-zA-Z]/, "Password must contain at least one letter")
      .regex(/\d/, "Password must contain at least one number"),
    confirmPassword: z.string(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords do not match",
    path: ["confirmPassword"],
  })

export type LoginInput = z.infer<typeof loginSchema>
export type RegisterInput = z.infer<typeof registerSchema>
export type ForgotPasswordInput = z.infer<typeof forgotPasswordSchema>
export type ResetPasswordInput = z.infer<typeof resetPasswordSchema>
